import React, { useEffect, useState } from 'react'

import Layout from '../components/Layout'
import { isEmail, normalizeEmail } from '../utils/validation'

//utils
import axios from 'axios'
import { Link as LinkRouter } from 'react-router-dom'

//mui
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  Grid,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  List,
  ListItem,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'

// Limits mirror the backend (summa-app-eng batchPlanner.js).
const MAX_BATCHES = 50
const MAX_CUSTOM_SAMPLES = 96
const CUSTOM = '__custom__'

// Assignment of the created batches: none, one subject (plain email, not a
// Shopify customer), or a study. Subject and study are exclusive by design.
const ASSIGN_NONE = 'none'
const ASSIGN_SUBJECT = 'subject'
const ASSIGN_STUDY = 'study'

// One label file per batch, from the same public ZPL export the batch page
// uses (plain anchor, no auth header). Spaced out so the browser does not
// collapse the clicks; Chrome may ask once to allow multiple downloads.
const DOWNLOAD_SPACING_MS = 300
const downloadLabels = (batchIds) => {
  batchIds.forEach((id, i) => {
    setTimeout(() => {
      const a = document.createElement('a')
      a.href = axios.defaults.baseURL + '/public/batchzpl/' + id
      a.download = id + '.zpl'
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }, i * DOWNLOAD_SPACING_MS)
  })
}

// Dropdown order: smallest kit first, then by name, then by title.
const compareProtocols = (a, b) =>
  (a.no_of_samples ?? 0) - (b.no_of_samples ?? 0) ||
  (a.name ?? '').localeCompare(b.name ?? '') ||
  (a.title ?? '').localeCompare(b.title ?? '')

// "<samples> · <name> · <title>". A missing name shows as a dash on purpose,
// so protocols that still lack one are visible and can be fixed in the data.
const protocolLabel = (p) =>
  `${p.no_of_samples ?? '?'} · ${p.name ?? '—'} · ${p.title ?? ''}`

const isIntInRange = (value, min, max) =>
  Number.isInteger(value) && value >= min && value <= max

export default function ProtocolsNewBatch() {
  const [protocols, setProtocols] = useState(null)
  const [protocolId, setProtocolId] = useState('')
  const [customSamples, setCustomSamples] = useState(8)
  const [count, setCount] = useState(1)
  const [submitting, setSubmitting] = useState(false)

  const [assignMode, setAssignMode] = useState(ASSIGN_NONE)
  const [subjectEmail, setSubjectEmail] = useState('')
  const [studies, setStudies] = useState(null) // null = not loaded yet
  const [studiesError, setStudiesError] = useState('')
  const [studyId, setStudyId] = useState('')
  const [autoDownload, setAutoDownload] = useState(false)

  // Confirmation dialog: either a list of created batch ids or an error text.
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [createdIds, setCreatedIds] = useState([])
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    axios
      .get('/regular/protocols')
      .then((response) => {
        const sorted = [...response.data].sort(compareProtocols)
        setProtocols(sorted)
        if (sorted.length > 0) setProtocolId(sorted[0].id)
      })
      .catch((error) => console.log(error.response?.data ?? error.message))
  }, [])

  useEffect(() => {
    if (assignMode !== ASSIGN_STUDY || studies !== null) return
    axios
      .get('/admin/studies')
      .then((response) => {
        setStudies(response.data)
        setStudiesError('')
        if (response.data.length > 0 && studyId === '') setStudyId(response.data[0].id)
      })
      .catch((error) => {
        setStudies([])
        setStudiesError(
          'Could not load studies: ' + (error.response?.data?.error ?? error.message),
        )
      })
  }, [assignMode, studies, studyId])

  const isCustom = protocolId === CUSTOM
  const customValid = !isCustom || isIntInRange(customSamples, 1, MAX_CUSTOM_SAMPLES)
  const countValid = isIntInRange(count, 1, MAX_BATCHES)
  const subjectValid = assignMode !== ASSIGN_SUBJECT || isEmail(subjectEmail)
  const studyValid = assignMode !== ASSIGN_STUDY || studyId !== ''
  const canGenerate =
    protocolId !== '' &&
    customValid &&
    countValid &&
    subjectValid &&
    studyValid &&
    !submitting

  const generateBatches = () => {
    const body = isCustom
      ? { customProtocol: { noOfSamples: customSamples } }
      : { protocolId }
    body.count = count
    if (assignMode === ASSIGN_SUBJECT) body.subjectEmail = normalizeEmail(subjectEmail)
    if (assignMode === ASSIGN_STUDY) body.studyId = studyId

    setSubmitting(true)
    axios
      .post('/admin/batches', body)
      .then((response) => {
        const ids = response.data.batchIds ?? []
        setCreatedIds(ids)
        setErrorMsg('')
        setShowConfirmation(true)
        if (autoDownload && ids.length > 0) downloadLabels(ids)
      })
      .catch((error) => {
        setCreatedIds([])
        setErrorMsg(
          'Something went wrong. Error: ' +
            (error.response?.data?.error ?? error.message),
        )
        setShowConfirmation(true)
      })
      .finally(() => setSubmitting(false))
  }

  // Number inputs: keep the raw number so validation can disable the button
  // instead of silently clamping what the operator typed.
  const toNumber = (e) => (e.target.value === '' ? NaN : Number(e.target.value))

  return (
    <Layout title="New Sample Set">
      <Grid container style={{ margin: '40px auto 40px auto' }}>
        <Grid size={2} />
        <Grid size={8}>
          <Card style={{ margin: '20px 20px 20px 10px' }}>
            <CardContent>
              <Typography
                variant="body1"
                display="inline"
                style={{ flex: 1, textAlign: 'left', marginRight: 10 }}
              >
                Select protocol. Number of samples per set will be taken from
                protocol definition. Choose "Custom protocol" for an ad-hoc kit
                and enter the sample count yourself.
              </Typography>
              <FormControl
                style={{ display: 'flex', fullWidth: true, marginTop: 20 }}
              >
                <Select
                  name="SampleType"
                  labelId="sampling-label"
                  id="sampling-select"
                  value={protocolId}
                  onChange={(e) => {
                    setProtocolId(e.target.value)
                  }}
                >
                  {protocols ? (
                    protocols.map((protocol) => (
                      <MenuItem key={protocol.id} value={protocol.id}>
                        {protocolLabel(protocol)}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">Loading protocols</MenuItem>
                  )}
                  <Divider />
                  <MenuItem value={CUSTOM}>Custom protocol…</MenuItem>
                </Select>
              </FormControl>

              {isCustom && (
                <TextField
                  label="Samples per batch"
                  type="number"
                  size="small"
                  value={Number.isNaN(customSamples) ? '' : customSamples}
                  onChange={(e) => setCustomSamples(toNumber(e))}
                  error={!customValid}
                  helperText={
                    customValid
                      ? 'Short ids will start at 1.'
                      : `Enter a whole number between 1 and ${MAX_CUSTOM_SAMPLES}.`
                  }
                  inputProps={{ min: 1, max: MAX_CUSTOM_SAMPLES, step: 1 }}
                  style={{ marginTop: 20 }}
                  fullWidth
                />
              )}

              <TextField
                label="Number of batches"
                type="number"
                size="small"
                value={Number.isNaN(count) ? '' : count}
                onChange={(e) => setCount(toNumber(e))}
                error={!countValid}
                helperText={
                  countValid
                    ? ''
                    : `Enter a whole number between 1 and ${MAX_BATCHES}.`
                }
                inputProps={{ min: 1, max: MAX_BATCHES, step: 1 }}
                style={{ marginTop: 20 }}
                fullWidth
              />

              <FormControl style={{ marginTop: 20 }}>
                <FormLabel id="assign-label">Assign created batches to</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="assign-label"
                  value={assignMode}
                  onChange={(e) => setAssignMode(e.target.value)}
                >
                  <FormControlLabel value={ASSIGN_NONE} control={<Radio />} label="Nobody yet" />
                  <FormControlLabel value={ASSIGN_SUBJECT} control={<Radio />} label="Subject" />
                  <FormControlLabel value={ASSIGN_STUDY} control={<Radio />} label="Study" />
                </RadioGroup>
              </FormControl>

              {assignMode === ASSIGN_SUBJECT && (
                <TextField
                  label="Subject email"
                  type="email"
                  size="small"
                  value={subjectEmail}
                  onChange={(e) => setSubjectEmail(e.target.value)}
                  error={subjectEmail !== '' && !subjectValid}
                  helperText={
                    subjectValid
                      ? 'Batches are created in @subject state for this email.'
                      : 'Enter a valid email address.'
                  }
                  style={{ marginTop: 10 }}
                  fullWidth
                />
              )}

              {assignMode === ASSIGN_STUDY && (
                <FormControl style={{ display: 'flex', marginTop: 10 }} error={studiesError !== ''}>
                  <Select
                    id="study-select"
                    size="small"
                    value={studyId}
                    displayEmpty
                    onChange={(e) => setStudyId(e.target.value)}
                  >
                    {studies === null ? (
                      <MenuItem value="">Loading studies…</MenuItem>
                    ) : studies.length === 0 ? (
                      <MenuItem value="">No studies found</MenuItem>
                    ) : (
                      studies.map((study) => (
                        <MenuItem key={study.id} value={study.id}>
                          {study.name ?? study.id} · {study.memberCount} subjects ·{' '}
                          {study.batchCount} batches
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  <Typography variant="caption" color={studiesError ? 'error' : 'textSecondary'}>
                    {studiesError || 'Batch ids are added to the study; batches stay in @new state.'}
                  </Typography>
                </FormControl>
              )}

              <FormControlLabel
                style={{ marginTop: 10, display: 'flex' }}
                control={
                  <Checkbox
                    checked={autoDownload}
                    onChange={(e) => setAutoDownload(e.target.checked)}
                  />
                }
                label="Download ZPL labels after creation (one file per batch)"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={2} />
      </Grid>
      <Grid container style={{ margin: '40px auto 40px auto' }}>
        <Grid size={2} />
        <Grid style={{ display: 'flex', justifyContent: 'center' }} size={8}>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={generateBatches}
            disabled={!canGenerate}
          >
            {submitting
              ? 'Generating…'
              : count > 1 && countValid
                ? `Generate ${count} new sets`
                : 'Generate new set'}
          </Button>
        </Grid>
        <Grid size={2} />
      </Grid>
      <Dialog open={showConfirmation}>
        <DialogTitle id="form-dialog-title">New sample set</DialogTitle>
        <DialogContent>
          {errorMsg ? (
            <DialogContentText>{errorMsg}</DialogContentText>
          ) : (
            <>
              <DialogContentText>
                {createdIds.length === 1
                  ? 'New batch has been successfully generated:'
                  : `${createdIds.length} new batches have been successfully generated:`}
              </DialogContentText>
              <List dense>
                {createdIds.map((id) => (
                  <ListItem key={id} disableGutters>
                    <Link component={LinkRouter} to={'/batch/' + id}>
                      {id}
                    </Link>
                  </ListItem>
                ))}
              </List>
              {autoDownload && (
                <DialogContentText variant="caption">
                  Label files are being downloaded; the browser may ask once to
                  allow multiple downloads from this site.
                </DialogContentText>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!errorMsg && createdIds.length > 0 && (
            <Button onClick={() => downloadLabels(createdIds)} color="primary">
              {autoDownload ? 'Download labels again' : 'Download labels'}
            </Button>
          )}
          <Button
            onClick={() => {
              setShowConfirmation(false)
            }}
            color="primary"
          >
            Back
          </Button>
          <Button component={LinkRouter} to={'/protocols'}>
            See sample sets
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
